<template>
  <div class="activities-page">
    <section class="activities-shell">
      <div class="activities-hero">
        <div class="hero-copy">
          <nav class="activities-breadcrumb" aria-label="Breadcrumb">
            <span>Home</span>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">Recent Activities</span>
          </nav>

          <p class="hero-kicker">Audit Stream</p>
          <h1>Track recent platform activity</h1>
          <p class="hero-description">
            Review time-stamped events, scan actor types quickly, and search the activity log
            through a cleaner administrative monitoring view.
          </p>
        </div>

        <div class="hero-metrics">
          <article class="metric-card">
            <span class="metric-label">Events loaded</span>
            <strong class="metric-value">{{ filteredActivities.length }}</strong>
            <span class="metric-meta">Records currently matching your search</span>
          </article>
          <article class="metric-card">
            <span class="metric-label">Activity feed</span>
            <strong class="metric-value">Live</strong>
            <span class="metric-meta">Ready for review and admin oversight</span>
          </article>
        </div>
      </div>

      <section class="toolbar">
        <div class="toolbar-copy">
          <p class="results-kicker">Monitoring</p>
          <h2>Recent activities</h2>
        </div>

        <div class="toolbar-actions">
          <label class="search-shell" for="activity-search">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input
              id="activity-search"
              v-model.trim="search"
              type="search"
              placeholder="Search activity messages or user types"
            />
          </label>
        </div>
      </section>

      <div v-if="loading" class="loading-state">
        <b-spinner />
        <p>Loading activity feed...</p>
      </div>

      <template v-else>
        <div v-if="paginatedActivities.length" class="activity-list">
          <article
            v-for="(item, index) in paginatedActivities"
            :key="item._id || index"
            class="activity-card"
          >
            <div class="card-glow"></div>

            <div class="activity-card__top">
              <div>
                <span class="activity-index"
                  >Event {{ padIndex((currentPage - 1) * perPage + index) }}</span
                >
                <h3>{{ item.user_type || 'Activity event' }}</h3>
              </div>
              <span class="timestamp-pill">{{ formatDate(item.created_at) }}</span>
            </div>

            <p class="activity-message">
              {{ item.message || 'No activity message was provided.' }}
            </p>

            <div class="activity-meta">
              <div class="meta-item">
                <span class="meta-label">Date and time</span>
                <strong>{{ formatDateTime(item.created_at) }}</strong>
              </div>
              <div class="meta-item">
                <span class="meta-label">User type</span>
                <strong>{{ item.user_type || 'Unknown' }}</strong>
              </div>
            </div>
          </article>
        </div>

        <div v-else class="empty-state">
          <h3>No activities found</h3>
          <p>There are no activity records matching the current search.</p>
        </div>

        <div v-if="filteredActivities.length" class="pagination-shell">
          <div class="pagination-copy">
            <label class="page-size-shell" for="activity-page-size">
              <span>Rows</span>
              <select id="activity-page-size" v-model="perPage">
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
              </select>
            </label>
            <p>Page {{ currentPage }} of {{ totalPages }}</p>
            <span
              >{{ filteredActivities.length }} result{{
                filteredActivities.length === 1 ? '' : 's'
              }}</span
            >
          </div>

          <b-pagination
            v-model="currentPage"
            :total-rows="filteredActivities.length"
            :per-page="perPage"
            pills
            align="right"
            aria-controls="activity-list"
          />
        </div>
      </template>
    </section>
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

export default {
  data() {
    return {
      loading: true,
      search: '',
      currentPage: 1,
      perPage: 10,
    };
  },

  computed: {
    ...mapGetters('admin', ['getActivities']),

    activities() {
      return [...(this.getActivities?.data?.results || [])].sort(
        (a, b) => (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0)
      );
    },

    filteredActivities() {
      const searchTerm = this.search.toLowerCase();

      return this.activities.filter((activity) => {
        const haystack = [activity.message, activity.user_type]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(searchTerm);
      });
    },

    paginatedActivities() {
      return this.filteredActivities.slice(
        (this.currentPage - 1) * this.perPage,
        this.currentPage * this.perPage
      );
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.filteredActivities.length / this.perPage));
    },
  },

  watch: {
    search() {
      this.currentPage = 1;
    },

    perPage() {
      this.currentPage = 1;
    },
  },

  methods: {
    ...mapActions('admin', ['GET_ACTIVITIES']),

    formatDate(value) {
      if (!value) return 'Date unavailable';
      return moment(String(value)).format('DD MMM YYYY');
    },

    formatDateTime(value) {
      if (!value) return 'Date unavailable';
      return moment(String(value)).format('DD MMM YYYY, hh:mm A');
    },

    padIndex(index) {
      return String(index + 1).padStart(2, '0');
    },
  },

  async mounted() {
    try {
      await this.GET_ACTIVITIES();
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style lang="scss" scoped>
.activities-page {
  min-height: 100vh;
  padding: 1.5rem 1rem 2rem;
  background: radial-gradient(circle at top left, rgba(228, 225, 240, 0.92), transparent 28%),
    radial-gradient(circle at top right, rgba(63, 51, 105, 0.12), transparent 24%),
    linear-gradient(180deg, #f5f0e8 0%, #f6f4fb 46%, #eef0f7 100%);
}

.activities-shell {
  max-width: 1320px;
  margin: 0 auto;
}

.activities-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(280px, 1fr);
  gap: 1.5rem;
  padding: 1.75rem;
  border: 1px solid rgba(48, 42, 82, 0.08);
  border-radius: 28px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(246, 245, 252, 0.9)), #fff;
  box-shadow: 0 24px 60px rgba(38, 34, 66, 0.1);
}

.activities-breadcrumb {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #706c84;
  font-size: 0.9rem;
}

.breadcrumb-separator {
  opacity: 0.55;
}

.breadcrumb-current {
  color: #31285d;
  font-weight: 600;
}

.hero-kicker,
.results-kicker {
  margin-bottom: 0.65rem;
  color: #5f4fb1;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-copy h1 {
  max-width: 14ch;
  margin-bottom: 0.85rem;
  color: #241d49;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(2rem, 4vw, 3.2rem);
  font-weight: 700;
  line-height: 1.04;
}

.hero-description {
  max-width: 62ch;
  margin-bottom: 0;
  color: #5d5873;
  font-size: 1rem;
  line-height: 1.75;
}

.hero-metrics {
  display: grid;
  gap: 1rem;
}

.metric-card {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 1.1rem 1.2rem;
  border: 1px solid rgba(48, 42, 82, 0.08);
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95), rgba(246, 245, 251, 0.9));
}

.metric-label {
  color: #726d88;
  font-size: 0.88rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.metric-value {
  color: #241d49;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 2rem;
  line-height: 1.1;
}

.metric-meta {
  color: #89829c;
  font-size: 0.92rem;
}

.toolbar {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1rem 1.1rem;
  border: 1px solid rgba(48, 42, 82, 0.08);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.84);
  backdrop-filter: blur(18px);
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  margin-left: auto;
}

.toolbar-copy h2 {
  margin-bottom: 0;
  color: #241d49;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(1.4rem, 2.4vw, 1.9rem);
  font-weight: 700;
}

.search-shell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: min(100%, 360px);
  margin-bottom: 0;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(48, 42, 82, 0.08);
  border-radius: 18px;
  background: #fafbfe;
  color: #7a7691;
}

.search-shell input {
  width: 100%;
  border: 0;
  outline: none;
  background: transparent;
  color: #31285d;
}

.page-size-shell {
  display: inline-flex;
  align-items: center;
  align-self: center;
  gap: 0.6rem;
  margin-bottom: 0;
  min-height: 3rem;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(48, 42, 82, 0.08);
  border-radius: 18px;
  background: #fafbfe;
  color: #655f7d;
  font-weight: 600;
}

.page-size-shell select {
  border: 0;
  outline: none;
  background: transparent;
  color: #31285d;
  line-height: 1;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 320px;
  margin-top: 1.5rem;
  padding: 2rem;
  border: 1px solid rgba(48, 42, 82, 0.08);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.9);
  text-align: center;
}

.loading-state p,
.empty-state p {
  margin-bottom: 0;
  color: #76718c;
}

.activity-list {
  display: grid;
  gap: 1rem;
  margin-top: 1.5rem;
}

.pagination-shell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.25rem;
  padding: 1rem 1.1rem;
  border: 1px solid rgba(48, 42, 82, 0.08);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.86);
}

.pagination-copy {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: #726d88;
  flex-wrap: wrap;
}

.pagination-copy p,
.pagination-copy span {
  display: inline-flex;
  align-items: center;
  min-height: 3rem;
  margin-bottom: 0;
}

::v-deep .pagination .page-item .page-link {
  border: 0;
  border-radius: 999px;
  margin: 0 0.2rem;
  color: #4a3da0;
  background: #efedfb;
}

::v-deep .pagination .page-item.active .page-link {
  background: linear-gradient(135deg, #5142aa, #7c6ae0);
  color: #fff;
  box-shadow: 0 10px 20px rgba(81, 66, 170, 0.18);
}

.activity-card {
  position: relative;
  overflow: hidden;
  padding: 1.4rem;
  border: 1px solid rgba(48, 42, 82, 0.08);
  border-radius: 24px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(247, 246, 252, 0.92)), #fff;
  box-shadow: 0 20px 38px rgba(34, 28, 60, 0.08);
}

.card-glow {
  position: absolute;
  top: -72px;
  right: -20px;
  width: 210px;
  height: 210px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(95, 79, 177, 0.12), transparent 68%);
  pointer-events: none;
}

.activity-card__top,
.activity-message,
.activity-meta {
  position: relative;
  z-index: 1;
}

.activity-card__top {
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 1rem;
}

.activity-index {
  display: inline-block;
  margin-bottom: 0.45rem;
  color: #8680a0;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.activity-card__top h3 {
  margin-bottom: 0;
  color: #2a2155;
  font-size: 1.26rem;
  font-weight: 700;
  line-height: 1.35;
}

.timestamp-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.8rem;
  border-radius: 999px;
  background: rgba(95, 79, 177, 0.1);
  color: #4a3da0;
  font-size: 0.82rem;
  font-weight: 700;
}

.activity-message {
  margin: 1rem 0 1.1rem;
  color: #5e5974;
  line-height: 1.75;
}

.activity-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem;
}

.meta-item {
  padding: 0.95rem 1rem;
  border: 1px solid rgba(48, 42, 82, 0.08);
  border-radius: 18px;
  background: rgba(248, 248, 252, 0.92);
}

.meta-label {
  display: block;
  margin-bottom: 0.35rem;
  color: #827d97;
  font-size: 0.82rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.meta-item strong {
  color: #2a2155;
  font-size: 0.96rem;
  line-height: 1.5;
}

@media (max-width: 991px) {
  .activities-page {
    padding-top: 0;
  }

  .activities-shell {
    margin-top: 0;
  }

  .activities-hero,
  .toolbar,
  .toolbar-actions,
  .activity-meta {
    grid-template-columns: 1fr;
    flex-direction: column;
    align-items: stretch;
  }
}

@media (max-width: 767px) {
  .activities-page {
    padding: 0;
  }

  .activities-shell {
    margin-top: 0;
  }

  .activities-hero,
  .toolbar,
  .activity-card,
  .pagination-shell,
  .loading-state,
  .empty-state {
    border-radius: 22px;
  }

  .activity-card__top {
    flex-direction: column;
  }

  .search-shell {
    min-width: 0;
    width: 100%;
  }
}

@media (max-width: 600px) {
  .activities-page {
    padding-top: 0;
  }

  .activities-shell {
    margin-top: 0;
  }
}
</style>
